import { useState, useEffect } from "react";
import { Button, Alert, LoadingOverlay, Group } from "@mantine/core";
import { PronosticV2Service } from "services/pronosticv2.service";
import type { Player, PredictionEntry } from "quiz/types/player.model";
import type { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";

interface PlayerQueryBySubPlacesProps {
  pronosticV2: PronosticV2;
}

interface WinnerInfo {
  subPlaceIndex: string;
  subPlaceTitle?: string;
  winner: Winner[];
}

interface Winner {
  id: string;
  mail: string;
  name: string;
  phoneNumber: string;
  rank: number;
  totalScore?: number;
}

export function PlayerQueryBySubPlaces({
  pronosticV2,
}: PlayerQueryBySubPlacesProps) {
  const [error, setError] = useState<string | null>(null);
  const [subPlaceIds, setSubPlaceIds] = useState<string[]>([]);
  const [isExportingPlayers, setIsExportingPlayers] = useState<boolean>(false);
  const [isExportingWinners, setIsExportingWinners] = useState<boolean>(false);
  const pronosticV2Id = pronosticV2.id;
  const winConditionType =
    pronosticV2.commonSettings.general.moduleSettings.winCondition.type;

  useEffect(() => {
    if (pronosticV2.commonSettings?.general?.moduleSettings?.place?.subPlaces) {
      const extractedSubPlaceIds =
        pronosticV2.commonSettings.general.moduleSettings.place.subPlaces.map(
          (sp: any) => sp.id,
        );
      setSubPlaceIds(extractedSubPlaceIds);
    }
  }, [pronosticV2]);

  const handleExportPlayers = async () => {
    if (subPlaceIds.length === 0) {
      setError("No subplaces available to export");
      return;
    }

    setIsExportingPlayers(true);
    setError(null);

    try {
      const service = new PronosticV2Service();
      const getAllPlayersByPronosticV2Data = {
        pronosticV2Id: pronosticV2Id,
        listOfSubPlaces: JSON.stringify(subPlaceIds),
      };

      const allPlayers = await service.getAllPlayersByPronosticV2(
        getAllPlayersByPronosticV2Data,
      );

      if (Array.isArray(allPlayers)) {
        exportPlayersDataToCsv(allPlayers as Player[], "csv");
      } else {
        setError("Unexpected data format received from service");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch player data",
      );
    } finally {
      setIsExportingPlayers(false);
    }
  };

  const handleExportWinners = async () => {
    if (!pronosticV2.specificSettings?.winnersInfo) {
      setError("No winners data available to export");
      return;
    }

    if (subPlaceIds.length === 0) {
      setError("No subplaces available to export");
      return;
    }

    setIsExportingWinners(true);
    setError(null);

    try {
      const service = new PronosticV2Service();
      const getAllPlayersByPronosticV2Data = {
        pronosticV2Id: pronosticV2Id,
        listOfSubPlaces: JSON.stringify(subPlaceIds),
      };

      const allPlayers = await service.getAllPlayersByPronosticV2(
        getAllPlayersByPronosticV2Data,
      );

      if (Array.isArray(allPlayers)) {
        const winnersInfo: WinnerInfo[] =
          pronosticV2.specificSettings.winnersInfo;
        exportWinnersDataToCsv(winnersInfo, allPlayers as Player[], "winners");
      } else {
        setError("Unexpected data format received from service");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch player data for winners",
      );
    } finally {
      setIsExportingWinners(false);
    }
  };

  const getSubPlaceName = (subPlaceId: string) => {
    const subPlace =
      pronosticV2.commonSettings.general.moduleSettings.place.subPlaces.find(
        (sp: any) => sp.id === subPlaceId,
      );
    return subPlace?.title || subPlaceId;
  };

  const getSubPlaceNameByIndex = (subPlaceIndex: string) => {
    const subPlace =
      pronosticV2.commonSettings.general.moduleSettings.place.subPlaces.find(
        (sp: any) => sp.id.toString() === subPlaceIndex,
      );
    return subPlace?.title || subPlaceIndex;
  };

  const exportPlayersDataToCsv = (
    playersData: Player[],
    filenameSuffix: string = "",
  ) => {
    if (!playersData || playersData.length === 0) {
      setError("No players data to export.");
      return;
    }

    const userPlayers = playersData.filter(
      // (player) => player.email && player.firstName,
      (player) => player.phoneNumber && player.firstName,
    );
    if (userPlayers.length === 0) {
      setError("No user players data to export.");
      return;
    }

    // Calculate max number of predictions across all players
    const maxPredictions = Math.max(
      ...userPlayers.map(
        (player) => player.pronosticInfo?.prediction?.length || 0,
      ),
      1, // Minimum of 1 columns
    );

    // Generate dynamic prediction headers
    const predictionHeaders = Array.from(
      { length: maxPredictions },
      (_, i) => `Prediction${i + 1}`,
    );

    // Define all headers (original form without Rank and Total Score)
    const headers = [
      "Subplace",
      "Firstname",
      "Lastname",
      "Email",
      "Phone",
      "Room (Salon/Loge)",
      "Enterprise",
      ...predictionHeaders,
    ];

    // Process each player
    const csvRows = userPlayers.map((player) => {
      // Create a map of predictions by their index
      const predictionMap = new Map<number, string>();
      player.pronosticInfo?.prediction?.forEach((pred: PredictionEntry) => {
        if (pred.values.length === 2) {
          predictionMap.set(pred.index, `${pred.values[0]}-${pred.values[1]}`);
        } else if (pred.values.length === 1) {
          predictionMap.set(pred.index, `${pred.values[0]}`);
        }
      });

      // Fill predictions in order (Prediction1, Prediction2, etc.)
      const predictionValues = Array.from(
        { length: maxPredictions },
        (_, i) => predictionMap.get(i + 1) || "",
      );

      // Special handling for long text fields
      const formatLongText = (text: string | undefined) => {
        if (!text) return "";
        if (/^\d+$/.test(text)) return text; // Keep numbers intact
        return text.length > 50 ? `${text.substring(0, 50)}...` : text;
      };

      return [
        getSubPlaceName(player.partitionId?.split("$")[1] || ""), //change to partitionId
        player.firstName || "",
        player.lastName || "",
        player.email || "",
        player.phoneNumber || "",
        formatLongText(player.room),
        formatLongText(player.enterprise),
        ...predictionValues,
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...csvRows.map((row) =>
        row
          .map((value) => {
            if (value === null || value === undefined) return "";
            const str = String(value);
            return `"${str.replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ].join("\n");

    // Trigger download
    downloadCsv(csvContent, `players_export${filenameSuffix}`);
  };

  const exportWinnersDataToCsv = (
    winnersInfo: WinnerInfo[],
    playersData: Player[],
    filenameSuffix: string = "",
  ) => {
    if (!winnersInfo || winnersInfo.length === 0) {
      setError("No winners data to export.");
      return;
    }

    if (!playersData || playersData.length === 0) {
      setError("No player data available for winners export.");
      return;
    }

    // Check if we should include total score column based on winConditionType
    const includeTotalScore = winConditionType === "PointsCalculation";

    // Calculate max number of predictions across all winners
    const maxPredictions = Math.max(
      ...winnersInfo.flatMap((subPlaceWinners) =>
        subPlaceWinners.winner.flatMap((winner) => {
          const player = playersData.find((p) => p.id === winner.id);
          return player?.pronosticInfo?.prediction?.length || 0;
        }),
      ),
      1, // Minimum of 3 columns
    );

    // Generate dynamic prediction headers
    const predictionHeaders = Array.from(
      { length: maxPredictions },
      (_, i) => `Prediction${i + 1}`,
    );

    // Define headers conditionally based on winConditionType
    const baseHeaders = [
      "Subplace",
      "Rank",
      ...(includeTotalScore ? ["Total Score"] : []),
      "Firstname",
      "Lastname",
      "Email",
      "Phone",
      "Room (Salon/Loge)",
      "Enterprise",
      ...predictionHeaders,
    ];

    // Process each winner entry from ALL subplaces
    const csvRows: string[][] = [];

    // Process winners from all subplaces
    winnersInfo.forEach((subPlaceWinners) => {
      // Use subPlaceTitle directly instead of subPlaceIndex
      const subPlaceName =
        //subPlaceWinners.subPlaceTitle ||
        getSubPlaceNameByIndex(subPlaceWinners.subPlaceIndex); //||
      //subPlaceWinners.subPlaceIndex;

      // Process each winner in this subplace
      subPlaceWinners.winner.forEach((winner) => {
        // Find the corresponding player by ID matching
        const player = playersData.find((p) => p.id === winner.id);

        if (!player) {
          // Fallback to email matching if ID matching fails
          const fallbackPlayer = playersData.find(
            //(p) => p.email === winner.mail,
            (p) => p.phoneNumber === winner.phoneNumber, //TO TEST
          );
          if (!fallbackPlayer) {
            return; // Skip this winner if no player found
          }
          processWinner(
            fallbackPlayer,
            winner,
            subPlaceName,
            csvRows,
            maxPredictions,
            includeTotalScore,
          );
          return;
        }

        processWinner(
          player,
          winner,
          subPlaceName,
          csvRows,
          maxPredictions,
          includeTotalScore,
        );
      });
    });

    if (csvRows.length === 0) {
      setError("No matching player data found for winners.");
      return;
    }

    // Create CSV content
    const csvContent = [
      baseHeaders.join(","),
      ...csvRows.map((row) =>
        row
          .map((value) => {
            if (value === null || value === undefined) return "";
            const str = String(value);
            return `"${str.replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ].join("\n");

    // Trigger download
    downloadCsv(csvContent, `winners_export${filenameSuffix}`);
  };

  const processWinner = (
    player: Player,
    winner: Winner,
    subPlaceName: string,
    csvRows: string[][],
    maxPredictions: number,
    includeTotalScore: boolean,
  ) => {
    // Split winner name into first and last name (use player data if available)
    const firstName = player.firstName || winner.name.split(" ")[0] || "";
    const lastName =
      player.lastName || winner.name.split(" ").slice(1).join(" ") || "";

    // Create a map of predictions by their index
    const predictionMap = new Map<number, string>();
    player.pronosticInfo?.prediction?.forEach((pred: PredictionEntry) => {
      if (pred.values.length === 2) {
        predictionMap.set(pred.index, `${pred.values[0]}-${pred.values[1]}`);
      } else if (pred.values.length === 1) {
        predictionMap.set(pred.index, `${pred.values[0]}`);
      }
    });

    // Fill predictions in order (Prediction1, Prediction2, etc.)
    const predictionValues = Array.from(
      { length: maxPredictions },
      (_, i) => predictionMap.get(i + 1) || "",
    );

    // Special handling for long text fields
    const formatLongText = (text: string | undefined) => {
      if (!text) return "";
      if (/^\d+$/.test(text)) return text; // Keep numbers intact
      return text.length > 50 ? `${text.substring(0, 50)}...` : text;
    };

    // Get totalScore from winner data or calculate from player data if available
    const totalScore = winner.totalScore || "";

    // Build row data conditionally based on includeTotalScore
    const rowData = [
      subPlaceName,
      winner.rank.toString(),
      ...(includeTotalScore ? [totalScore.toString()] : []),
      firstName,
      lastName,
      winner.mail || player.email || "",
      winner.phoneNumber || player.phoneNumber || "",
      formatLongText(player.room),
      formatLongText(player.enterprise),
      ...predictionValues,
    ];

    csvRows.push(rowData);
  };

  const downloadCsv = (csvContent: string, baseFilename: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${baseFilename}_${pronosticV2Id}_${new Date().toISOString().slice(0, 10)}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // Clear any previous errors
    setError(null);
  };

  return (
    <>
      {error && (
        <Alert color="red" mb="sm">
          {error}
        </Alert>
      )}

      <div>
        <LoadingOverlay visible={isExportingPlayers || isExportingWinners} />
        <Group className="mt-2">
          <Button
            onClick={handleExportPlayers}
            loading={isExportingPlayers}
            className="bg-gray-400 disabled:opacity-75 text-black"
            disabled={subPlaceIds.length === 0}
            size="xs"
          >
            Export All Players
          </Button>
          <Button
            onClick={handleExportWinners}
            loading={isExportingWinners}
            className="bg-gray-400 disabled:opacity-75 text-black"
            disabled={
              !pronosticV2.specificSettings?.winnersInfo ||
              subPlaceIds.length === 0
            }
            size="xs"
          >
            Export Winners
          </Button>
        </Group>
      </div>
    </>
  );
}
